import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  OverlayTrigger,
  Popover,
  Tooltip,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { ValidationRules, validateForm, hasErrors, formatErrors } from '../utils/validation';
import { createTooltipProps } from '../utils/fieldTooltips';
import MissingSelectOption from '../components/MissingSelectOption';
import GridPagination from '../components/GridPagination';

// ValidatedFormControl defined outside to prevent recreation on every render
const ValidatedFormControl = React.memo(
  ({
    name,
    label,
    required,
    children,
    tooltipDescription,
    validationErrors = {},
    formData = {},
    handleChange,
    ...props
  }) => {
    const hasError = validationErrors[name];
    const popover = hasError ? (
      <Popover id={`popover-${name}`}>
        <Popover.Header as="h3">Validation Error</Popover.Header>
        <Popover.Body>{validationErrors[name]}</Popover.Body>
      </Popover>
    ) : null;

    const tooltip = tooltipDescription ? (
      <Tooltip id={`tooltip-${name}`}>{tooltipDescription}</Tooltip>
    ) : null;

    const formControl = children || (
      <Form.Control
        {...props}
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        isInvalid={hasError}
      />
    );

    const wrappedControl = hasError ? (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popover}>
        {formControl}
      </OverlayTrigger>
    ) : tooltipDescription ? (
      <OverlayTrigger placement="top" overlay={tooltip}>
        {formControl}
      </OverlayTrigger>
    ) : (
      formControl
    );

    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {label} {required && <span className="text-danger">*</span>}
        </Form.Label>
        {wrappedControl}
      </Form.Group>
    );
  }
);

function CustomerContactModal({ customerContact, onClose, onSave }) {
  const [modalError, setModalError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    account_no: '',
    title: '',
    first_name: '',
    surname: '',
    telephone: '',
    fax: '',
    mobile: '',
    email: '',
    main: true,
    marketing: true,
    job_title: '',
    comm_method: '',
    salutation: '',
    site_name: '',
    notes: '',
  });
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    if (customerContact) {
      setFormData({
        account_no: customerContact.account_no || '',
        title: customerContact.title || '',
        first_name: customerContact.first_name || '',
        surname: customerContact.surname || '',
        telephone: customerContact.telephone || '',
        fax: customerContact.fax || '',
        mobile: customerContact.mobile || '',
        email: customerContact.email || '',
        main: customerContact.main !== undefined ? customerContact.main : true,
        marketing: customerContact.marketing !== undefined ? customerContact.marketing : true,
        job_title: customerContact.job_title || '',
        comm_method: customerContact.comm_method || '',
        salutation: customerContact.salutation || '',
        site_name: customerContact.site_name || '',
        notes: customerContact.notes || '',
      });
    } else {
      // Reset form for new customer contact
      setFormData({
        account_no: '',
        title: '',
        first_name: '',
        surname: '',
        telephone: '',
        fax: '',
        mobile: '',
        email: '',
        main: true,
        marketing: true,
        job_title: '',
        comm_method: '',
        salutation: '',
        site_name: '',
        notes: '',
      });
    }
  }, [customerContact]);

  useEffect(() => {
    // Fetch customers for dropdown
    const fetchCustomers = async () => {
      try {
        const response = await axios.get('/api/customers?limit=1000');
        // Extract unique account numbers and names from customers
        const customerData = response.data.customers || response.data || [];
        setCustomers(customerData);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      }
    };

    fetchCustomers();
  }, []);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    const updatedFormData = {
      ...formData,
      [e.target.name]: value,
    };

    setFormData(updatedFormData);

    // Clear validation errors for this field when user starts typing
    if (validationErrors[e.target.name]) {
      const newErrors = { ...validationErrors };
      delete newErrors[e.target.name];
      setValidationErrors(newErrors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError(''); // Clear any previous errors

    // Comprehensive validation using validation rules
    const errors = validateForm(formData, ValidationRules.customerContact);
    setValidationErrors(errors);

    if (hasErrors(errors)) {
      setModalError(formatErrors(errors));
      return;
    }

    try {
      await onSave(formData);
      // If successful, onSave should close the modal
    } catch (error) {
      setModalError(error.message || 'Failed to save customer contact');
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {customerContact ? 'Edit Customer Contact' : 'Add New Customer Contact'}
        </Modal.Title>
      </Modal.Header>

      {modalError && (
        <div className="alert alert-danger m-3 mb-0">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {modalError}
        </div>
      )}

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Tabs defaultActiveKey="contact" className="mb-3">
            <Tab eventKey="contact" title="Contact">
              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="account_no"
                    label="Account No"
                    required={true}
                    tooltipDescription="Customer account number for contact association"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  >
                    <Form.Select
                      name="account_no"
                      value={formData.account_no}
                      onChange={handleChange}
                      isInvalid={!!validationErrors.account_no}
                    >
                      <option value="">Select a customer...</option>
                      <MissingSelectOption
                        value={formData.account_no}
                        options={customers}
                        getOptionValue={(customer) => customer.account_no}
                      />
                      {customers.map((customer) => (
                        <option key={customer.account_no} value={customer.account_no}>
                          {customer.account_no} - {customer.name}
                        </option>
                      ))}
                    </Form.Select>
                  </ValidatedFormControl>
                </Col>
                <Col md={6}>
                  <ValidatedFormControl
                    name="title"
                    label="Title"
                    type="text"
                    tooltipDescription="Contact title (Mr, Mrs, Dr, etc.)"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="first_name"
                    label="First Name"
                    type="text"
                    tooltipDescription="Contact's first name"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={6}>
                  <ValidatedFormControl
                    name="surname"
                    label="Surname"
                    required={true}
                    type="text"
                    tooltipDescription="Contact's surname (required)"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <ValidatedFormControl
                    name="telephone"
                    label="Telephone"
                    type="tel"
                    tooltipDescription="Contact's telephone number"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={4}>
                  <ValidatedFormControl
                    name="fax"
                    label="Fax"
                    type="tel"
                    tooltipDescription="Contact's fax number"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={4}>
                  <ValidatedFormControl
                    name="mobile"
                    label="Mobile"
                    type="tel"
                    tooltipDescription="Contact's mobile number"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="email"
                    label="Email"
                    type="email"
                    tooltipDescription="Contact's email address"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={6}>
                  <ValidatedFormControl
                    name="site_name"
                    label="Site Name"
                    type="text"
                    tooltipDescription="Associated site name"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>
            </Tab>
            <Tab eventKey="details" title="Details">
              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="job_title"
                    label="Job Title"
                    type="text"
                    tooltipDescription="Contact's job title"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={6}>
                  <ValidatedFormControl
                    name="comm_method"
                    label="Communication Method"
                    type="text"
                    tooltipDescription="Preferred communication method"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <ValidatedFormControl
                    name="salutation"
                    label="Salutation"
                    type="text"
                    tooltipDescription="Formal salutation for correspondence"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
                <Col md={6}>
                  <div className="d-flex gap-4">
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Main Contact
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('main')}>
                              Is this the main contact for this customer? (Defaults to Yes)
                            </Tooltip>
                          }
                        >
                          <i
                            className="bi bi-question-circle ms-2 text-muted"
                            style={{ cursor: 'help' }}
                          ></i>
                        </OverlayTrigger>
                      </Form.Label>
                      <Form.Check
                        type="checkbox"
                        name="main"
                        checked={formData.main}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.main}
                        {...createTooltipProps('main')}
                      />
                      {validationErrors.main && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.main}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Marketing
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip {...createTooltipProps('marketing')}>
                              Can this contact receive marketing communications? (Defaults to Yes)
                            </Tooltip>
                          }
                        >
                          <i
                            className="bi bi-question-circle ms-2 text-muted"
                            style={{ cursor: 'help' }}
                          ></i>
                        </OverlayTrigger>
                      </Form.Label>
                      <Form.Check
                        type="checkbox"
                        name="marketing"
                        checked={formData.marketing}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.marketing}
                        {...createTooltipProps('marketing')}
                      />
                      {validationErrors.marketing && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.marketing}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col>
                  <ValidatedFormControl
                    name="notes"
                    label="Notes"
                    as="textarea"
                    rows={3}
                    tooltipDescription="Additional notes about this contact"
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {customerContact ? 'Update Contact' : 'Add Contact'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function CustomerContacts() {
  const [customerContacts, setCustomerContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomerContact, setEditingCustomerContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0,
  });

  const fetchCustomerContacts = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...(search && { search }),
      });

      const response = await axios.get(`/api/customer-contacts?${params}`);
      setCustomerContacts(response.data.customerContacts || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error('Error fetching customer contacts:', error);
      setError('Failed to fetch customer contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerContacts();
  }, []);

  const handleAddCustomerContact = () => {
    setEditingCustomerContact(null);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleEditCustomerContact = (customerContact) => {
    setEditingCustomerContact(customerContact);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleSaveCustomerContact = async (customerContactData) => {
    console.log('Saving customer contact data:', customerContactData);

    try {
      if (editingCustomerContact) {
        console.log('Updating customer contact ID:', editingCustomerContact.id);
        const response = await axios.put(
          `/api/customer-contacts/${editingCustomerContact.id}`,
          customerContactData
        );
        console.log('Update response:', response.data);
        setSuccess('Customer contact updated successfully');
      } else {
        console.log('Creating new customer contact');
        const response = await axios.post('/api/customer-contacts', customerContactData);
        console.log('Create response:', response.data);
        setSuccess('Customer contact created successfully');
      }

      setShowModal(false);
      setEditingCustomerContact(null); // Clear editing state
      await fetchCustomerContacts(pagination.page, searchTerm);
    } catch (error) {
      console.error('Error saving customer contact:', error);

      // Check if it's a validation error (400 status)
      if (error.response && error.response.status === 400) {
        // For validation errors, throw the error so the modal can handle it
        throw new Error(error.response.data.error || 'Validation error occurred');
      } else {
        // Close modal for other errors and show on main page
        setShowModal(false);
        setEditingCustomerContact(null);
        setError(error.response?.data?.error || 'Failed to save customer contact');
      }
    }
  };

  const handleDeleteCustomerContact = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer contact?')) {
      try {
        await axios.delete(`/api/customer-contacts/${id}`);
        setSuccess('Customer contact deleted successfully');
        await fetchCustomerContacts(pagination.page, searchTerm);
      } catch (error) {
        console.error('Error deleting customer contact:', error);
        setError('Failed to delete customer contact');
      }
    }
  };

  const handleDeleteAllCustomerContacts = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL customer contacts? This action cannot be undone.'
      )
    ) {
      try {
        await axios.delete('/api/customer-contacts');
        setSuccess('All customer contacts deleted successfully');
        await fetchCustomerContacts(pagination.page, searchTerm);
      } catch (error) {
        console.error('Error deleting all customer contacts:', error);
        setError('Failed to delete all customer contacts');
      }
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get('/api/csv/export/customer-contacts', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer-contacts.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Customer contacts exported successfully');
    } catch (error) {
      console.error('Error exporting customer contacts:', error);
      setError('Failed to export customer contacts');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomerContacts(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchCustomerContacts(newPage, searchTerm);
  };

  const formatBoolean = (value) => {
    return value ? 'Yes' : 'No';
  };

  if (loading) {
    return (
      <Container fluid>
        <Row className="mt-0 mb-2">
          <Col>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '400px' }}
            >
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading customer contacts...</p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                <i
                  className="fas fa-address-book me-3 text-info"
                  style={{ fontSize: '2.5rem' }}
                ></i>
                <div>
                  <h1 className="mb-0">Customer Contacts</h1>
                  <p className="text-muted mb-0">
                    Manage customer contact information and preferences
                  </p>
                </div>
              </div>
            </div>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Button variant="primary" onClick={handleAddCustomerContact} className="me-2">
                Add Contact
              </Button>
              <Button
                variant="success"
                onClick={handleExportCSV}
                disabled={customerContacts.length === 0}
                className="me-2"
              >
                <i className="fas fa-download me-1"></i>
                Export CSV
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAllCustomerContacts}
                disabled={customerContacts.length === 0}
              >
                Delete All
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle me-2"></i>
          {success}
        </div>
      )}

      <Card>
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form onSubmit={handleSearch}>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    placeholder="Search contacts by account, name, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="me-2"
                  />
                  <Button variant="outline-primary" type="submit">
                    Search
                  </Button>
                </div>
              </Form>
            </Col>
            <Col md={6} className="text-end">
              <small className="text-muted">
                Showing {customerContacts.length} of {pagination.total} contacts
              </small>
            </Col>
          </Row>

          <div className="table-responsive" style={{ marginLeft: 0, marginRight: 0 }}>
            <Table striped bordered hover className="mb-3" style={{ marginLeft: 0 }}>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Name</th>
                  <th>Contact Info</th>
                  <th>Job Title</th>
                  <th>Site</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customerContacts.map((customerContact) => (
                  <tr key={customerContact.id}>
                    <td>
                      <strong>{customerContact.account_no}</strong>
                    </td>
                    <td>
                      <div>
                        <strong>
                          {customerContact.first_name} {customerContact.surname}
                        </strong>
                        {customerContact.title && (
                          <div className="text-muted small">{customerContact.title}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {customerContact.telephone && (
                          <div>
                            <i className="fas fa-phone me-1"></i>
                            {customerContact.telephone}
                          </div>
                        )}
                        {customerContact.mobile && (
                          <div>
                            <i className="fas fa-mobile-alt me-1"></i>
                            {customerContact.mobile}
                          </div>
                        )}
                        {customerContact.email && (
                          <div>
                            <i className="fas fa-envelope me-1"></i>
                            {customerContact.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{customerContact.job_title || '-'}</td>
                    <td>{customerContact.site_name || '-'}</td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEditCustomerContact(customerContact)}
                        className="me-2"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteCustomerContact(customerContact.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <GridPagination
            page={pagination.page}
            pages={pagination.pages}
            onPageChange={handlePageChange}
          />
        </Card.Body>
      </Card>

      {showModal && (
        <CustomerContactModal
          customerContact={editingCustomerContact}
          onClose={() => {
            setShowModal(false);
            setEditingCustomerContact(null);
            setError('');
            setSuccess('');
          }}
          onSave={handleSaveCustomerContact}
        />
      )}
    </Container>
  );
}

export default CustomerContacts;
